/**
 * LessonCompletedStage Component
 *
 * 레슨 완료 전용 화면
 * Sequential Mode에서 모든 필수 컴포넌트 완료 후 표시
 * - 학습 결과: 백엔드 API에서 조회 (video/quiz)
 * - 진행 상황: classCourse.progressSummary 사용 (대시보드와 동일)
 * - 다음 레슨: 백엔드 API의 nextLesson 사용 (순서 기반)
 */

import { useTranslation } from 'react-i18next'
import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { useQuery } from '@tanstack/react-query'
import { cn } from '@/lib/utils'
import {
    Trophy,
    ChevronRight,
    ArrowLeft,
    Clock,
    CheckCircle2,
    TrendingUp,
    Loader2,
} from 'lucide-react'
import { learningSessionApi } from '@/api/learningSession'
import type { ContentProgressSummary } from '@/api/b2b'

export interface LessonCompletedStageProps {
    /** 레슨 ID */
    lessonId: number
    /** 클래스 상품 ID (B2B) */
    classProductId?: number | null
    /** 전체 진행률 (classCourse.progressSummary) */
    progressSummary?: ContentProgressSummary | null
    /** 레슨 개요로 이동 */
    onBackToOverview: () => void
    /** 다음 레슨 시작 (nextLessonId 전달) */
    onNextLesson?: (nextLessonId: number) => void
    /** 모듈 마무리 퀴즈로 이동 (moduleId 전달) */
    onModuleCheckupQuiz?: (moduleId: number) => void
    /** Optional className */
    className?: string
}

/**
 * 초를 분/초로 변환 (예: 83 → "1분 23초", 23 → "23초", 120 → "2분")
 */
function formatDuration(seconds: number, t: (key: string, opts?: Record<string, unknown>) => string): string {
    const totalSeconds = Math.round(seconds)
    const mins = Math.floor(totalSeconds / 60)
    const secs = totalSeconds % 60

    if (mins === 0) return t('learning.lessonCompleted.seconds', { secs })
    if (secs === 0) return t('learning.lessonCompleted.minutes', { mins })
    return t('learning.lessonCompleted.minutesSeconds', { mins, secs })
}

export function LessonCompletedStage({
    lessonId,
    classProductId,
    progressSummary,
    onBackToOverview,
    onNextLesson,
    onModuleCheckupQuiz,
    className,
}: LessonCompletedStageProps) {
    const { t } = useTranslation('learner')
    // 백엔드에서 레슨 완료 결과 조회 (video/quiz + nextLesson)
    const { data: summary, isLoading, error } = useQuery({
        queryKey: ['lessonCompletionSummary', lessonId, classProductId],
        queryFn: () => learningSessionApi.getLessonCompletionSummary(lessonId, classProductId),
        staleTime: 30 * 1000,
    })

    if (isLoading) {
        return (
            <div className="flex justify-center items-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
        )
    }

    if (error || !summary) {
        return (
            <div className="py-20 text-center">
                <p className="text-gray-500">{t('learning.lessonCompleted.loadFailed')}</p>
                <button
                    onClick={onBackToOverview}
                    className="mt-4 text-primary-600 hover:text-primary-700"
                >
                    {t('learning.lessonCompleted.backToOverview')}
                </button>
            </div>
        )
    }

    const hasQuizData = summary.quizTotalCount > 0
    // 다음 레슨: 백엔드 API의 nextLesson 사용 (커리큘럼 컨텍스트 내 다음 레슨)
    const hasNextLesson = !!summary.nextLesson
    // 모듈 마무리 퀴즈: 현재 레슨이 모듈의 마지막 레슨일 때
    const hasModuleCheckupQuiz = !!summary.moduleCheckupQuiz

    return (
        <div className={cn(
            'px-4 py-8 mx-auto max-w-2xl duration-300 animate-in fade-in zoom-in-95',
            className
        )}>
            {/* Success Icon */}
            <div className="flex justify-center mb-6">
                <div className="flex justify-center items-center w-24 h-24 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full shadow-lg">
                    <Trophy className="w-14 h-14 text-white" />
                </div>
            </div>

            {/* Title */}
            <div className="mb-8 text-center">
                <h1 className="mb-3 text-3xl font-bold text-gray-900">
                    {t('learning.lessonCompleted.title')}
                </h1>
                <p className="text-lg text-gray-600">
                    <span className="font-semibold text-gray-900">
                        <ContentMetadataText text={summary.lessonTitle} />
                    </span>
                    <br />
                    {t('learning.lessonCompleted.lessonDone')}
                </p>
            </div>

            {/* 학습 결과 Card */}
            <div className="overflow-hidden mb-6 bg-white rounded-2xl border border-gray-200 shadow-sm">
                <div className="p-6">
                    <h2 className="flex gap-2 items-center mb-4 text-lg font-bold text-gray-900">
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                        {t('learning.lessonCompleted.learningResult')}
                    </h2>

                    <div className="space-y-3">
                        {/* 퀴즈 풀기 결과 */}
                        {hasQuizData && (
                            <div className="flex justify-between items-center py-3 border-b border-gray-100">
                                <span className="flex gap-2 items-center text-gray-600">
                                    <span className="text-xl">📝</span>
                                    {t('learning.lessonCompleted.quizResult')}
                                </span>
                                <span className="text-lg font-bold text-gray-900">
                                    {summary.quizCorrectCount} / {summary.quizTotalCount}
                                </span>
                            </div>
                        )}

                        {/* 학습한 시간 */}
                        <div className="flex justify-between items-center py-3">
                            <span className="flex gap-2 items-center text-gray-600">
                                <Clock className="w-5 h-5" />
                                {t('learning.lessonCompleted.studyTime')}
                            </span>
                            <span className="text-lg font-bold text-gray-900">
                                {formatDuration(summary.learningDurationSeconds, t)}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* 학습 진행 상황 Card - progressSummary from props (대시보드 데이터) */}
            {progressSummary && (
                <div className="overflow-hidden mb-8 bg-white rounded-2xl border border-gray-200 shadow-sm">
                    <div className="p-6">
                        <h2 className="flex gap-2 items-center mb-4 text-lg font-bold text-gray-900">
                            <TrendingUp className="w-5 h-5 text-primary-600" />
                            {t('learning.lessonCompleted.progressStatus')}
                        </h2>

                        {/* Progress Circle + Stats */}
                        <div className="flex gap-6 items-center">
                            {/* Circular Progress */}
                            <div className="relative w-24 h-24 shrink-0">
                                <svg className="w-24 h-24 transform -rotate-90">
                                    <circle
                                        cx="48"
                                        cy="48"
                                        r="40"
                                        stroke="#e5e7eb"
                                        strokeWidth="8"
                                        fill="none"
                                    />
                                    <circle
                                        cx="48"
                                        cy="48"
                                        r="40"
                                        stroke="#4f46e5"
                                        strokeWidth="8"
                                        fill="none"
                                        strokeLinecap="round"
                                        strokeDasharray={`${2 * Math.PI * 40}`}
                                        strokeDashoffset={`${2 * Math.PI * 40 * (1 - progressSummary.overallProgressPercent / 100)}`}
                                        className="transition-all duration-500"
                                    />
                                </svg>
                                <div className="flex absolute inset-0 justify-center items-center">
                                    <span className="text-xl font-bold text-gray-900">
                                        {progressSummary.overallProgressPercent}%
                                    </span>
                                </div>
                            </div>

                            {/* Stats */}
                            <div className="flex-1 space-y-2">
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">{t('learning.lessonCompleted.completedModules')}</span>
                                    <span className="font-semibold text-gray-900">
                                        {progressSummary.completedModules} / {progressSummary.totalModules}
                                    </span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">{t('learning.lessonCompleted.completedLessons')}</span>
                                    <span className="font-semibold text-gray-900">
                                        {progressSummary.completedLessons} / {progressSummary.totalLessons}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* 다음 액션 안내 */}
            {hasModuleCheckupQuiz && (
                <p className="mb-4 text-center text-gray-600">
                    {t('learning.lessonCompleted.tryModuleQuiz')}
                </p>
            )}
            {import.meta.env.DEV && !hasModuleCheckupQuiz && hasNextLesson && (
                <p className="mb-4 text-center text-gray-600">
                    {t('learning.lessonCompleted.startNextLesson')}
                </p>
            )}

            {/* Navigation Buttons */}
            <div className="flex flex-col gap-3 justify-center sm:flex-row">
                <button
                    onClick={onBackToOverview}
                    className="flex gap-2 justify-center items-center px-6 py-3 font-semibold text-gray-700 bg-white rounded-xl border-2 border-gray-200 transition-all hover:bg-gray-50 hover:border-gray-300"
                >
                    <ArrowLeft className="w-5 h-5" />
                    <span>{t('learning.lessonCompleted.toCourseList')}</span>
                </button>

                {hasModuleCheckupQuiz && onModuleCheckupQuiz && summary.moduleCheckupQuiz && (
                    <button
                        onClick={() => onModuleCheckupQuiz(summary.moduleCheckupQuiz!.moduleId)}
                        className="flex items-center justify-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 font-semibold transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                    >
                        <span>{t('learning.lessonCompleted.moduleEndQuiz')}</span>
                        <ChevronRight className="w-5 h-5" />
                    </button>
                )}

                {import.meta.env.DEV && !hasModuleCheckupQuiz && hasNextLesson && onNextLesson && summary.nextLesson && (
                    <button
                        onClick={() => onNextLesson(summary.nextLesson!.lessonId)}
                        className="flex items-center justify-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 font-semibold transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                    >
                        <span>{summary.nextLesson!.lessonId} <ContentMetadataText text={summary.nextLesson!.title} /></span>
                        <ChevronRight className="w-5 h-5" />
                    </button>
                )}
            </div>
        </div>
    )
}

// Legacy export for backward compatibility (프론트엔드 누적 방식 - deprecated)
export interface SessionStats {
    sessionStartTime: Date
    videoWatchedSeconds: number
    quizCorrectCount: number
    quizTotalCount: number
}
